"use client"

import { useMemo, useState } from 'react'
import type {
  EmailMessageAddress,
  EmailMessageMetadata,
} from '@/contracts/emailMessage'
import {
  selectAccessState,
  selectMainAttachmentsState,
  selectMainState,
  selectParentsMenu,
  type MainAttachmentState,
} from '@/msgdeskRuntime/state'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { ContentView } from '@/app/richEdit/content'
import { LockedScreen } from '@/deskUiSkeletons/Blocking/LockedScreen'
import { EmptyContent } from '@/deskUiSkeletons/Page/MainContent'
import { legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'

function scalar(value: unknown): string {
  return value === null || value === undefined ? '' : String(value).trim()
}

function tupleAddresses(value: unknown): EmailMessageAddress[] {
  if (!Array.isArray(value)) return []
  return value.flatMap(item => {
    if (!Array.isArray(item)) return []
    const address = scalar(item[1] ?? item[0])
    if (!address) return []
    return [{ name: scalar(item[0]), address }]
  })
}

function addressLabel(address: EmailMessageAddress): string {
  return address.name && address.name !== address.address
    ? `${address.name} <${address.address}>`
    : address.address
}

function AddressLine({
  label,
  addresses,
}: {
  label: string
  addresses: EmailMessageAddress[]
}) {
  if (!addresses.length) return null
  return (
    <div className="grid grid-cols-[52px_minmax(0,1fr)] gap-2 text-[13px] leading-5">
      <div className="font-medium text-[#667085]">{label}</div>
      <div className="break-words text-[#273548]">
        {addresses.map((address, index) => (
          <span key={`${address.address}-${index}`}>
            {index > 0 ? ', ' : ''}
            <a className="text-[#275f9f] hover:underline" href={`mailto:${address.address}`}>
              {addressLabel(address)}
            </a>
          </span>
        ))}
      </div>
    </div>
  )
}

function formatDate(timestamp: number, fallback: unknown): string {
  if (timestamp > 0) {
    const date = new Date(timestamp * 1000)
    if (!Number.isNaN(date.valueOf())) return date.toLocaleString()
  }
  return scalar(fallback)
}

function formatBytes(value: number): string {
  if (!(value > 0)) return ''
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}

function initials(addresses: EmailMessageAddress[], fallback: string): string {
  const source = addresses[0]?.name || addresses[0]?.address || fallback
  return source.slice(0, 1).toUpperCase() || 'E'
}

function AttachmentList({ attachments }: { attachments: MainAttachmentState[] }) {
  if (!attachments.length) return null
  return (
    <section className="border-t border-[#dde3ea] px-5 py-5 sm:px-8">
      <h2 className="mb-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-[#596779]">
        Attachments ({attachments.length})
      </h2>
      <div className="grid gap-2 sm:grid-cols-2">
        {attachments.map(attachment => (
          <a
            key={attachment.id}
            href={attachment.src}
            target="_blank"
            rel="noreferrer"
            className="flex min-w-0 items-center gap-3 rounded-lg border border-[#d9e0e8] bg-[#f8fafc] p-3 text-[#25364a] hover:border-[#afbecd] hover:bg-white"
          >
            <img
              src={attachment.thumbSrc}
              alt=""
              className="h-11 w-11 flex-none rounded border border-[#d9e0e8] bg-white object-cover"
            />
            <span className="min-w-0 truncate text-[13px] font-medium">
              {attachment.filename || attachment.title || 'Attachment'}
            </span>
          </a>
        ))}
      </div>
    </section>
  )
}

function TechnicalDetails({ message }: { message: EmailMessageMetadata }) {
  const verdicts = Object.entries(message.verdicts).filter(([, value]) => value)
  return (
    <details className="border-t border-[#dde3ea] px-5 py-4 text-[12px] text-[#48586b] sm:px-8">
      <summary className="cursor-pointer select-none font-semibold text-[#33465b]">
        Message details and headers
      </summary>
      <dl className="mt-4 grid grid-cols-[120px_minmax(0,1fr)] gap-x-3 gap-y-2">
        {message.rfcMessageId ? <><dt>Message-ID</dt><dd className="break-all">{message.rfcMessageId}</dd></> : null}
        {message.sesMessageId ? <><dt>SES message ID</dt><dd className="break-all">{message.sesMessageId}</dd></> : null}
        {message.inReplyTo ? <><dt>In-Reply-To</dt><dd className="break-all">{message.inReplyTo}</dd></> : null}
        {message.references.length ? <><dt>References</dt><dd className="break-all">{message.references.join(' ')}</dd></> : null}
        {message.envelopeFrom ? <><dt>Envelope from</dt><dd className="break-all">{message.envelopeFrom}</dd></> : null}
        {message.envelopeRecipients.length ? <><dt>Envelope to</dt><dd className="break-all">{message.envelopeRecipients.join(', ')}</dd></> : null}
        {message.priority ? <><dt>Priority</dt><dd>{message.priority}</dd></> : null}
        {message.rawSize ? <><dt>Raw size</dt><dd>{formatBytes(message.rawSize)}</dd></> : null}
        {message.deliveries?.length ? (
          <>
            <dt>Delivery</dt>
            <dd className="space-y-1">
              {message.deliveries.map(delivery => (
                <div key={delivery.id}>
                  {delivery.recipient}: {delivery.status.replaceAll('_', ' ')}
                  {delivery.statusCode ? ` (${delivery.statusCode})` : ''}
                </div>
              ))}
            </dd>
          </>
        ) : null}
        {verdicts.length ? (
          <>
            <dt>SES verdicts</dt>
            <dd>{verdicts.map(([key, value]) => `${key}: ${value}`).join(' · ')}</dd>
          </>
        ) : null}
      </dl>
      {message.headerLines.length ? (
        <pre className="mt-4 max-h-72 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-[#f3f5f7] p-4 font-mono text-[11px] leading-5 text-[#364152]">
          {message.headerLines.map(header => header.line).join('\n')}
        </pre>
      ) : null}
    </details>
  )
}

export interface EmailMessageViewModel {
  subject: string
  body: string
  bodyHtml: string
  datetime: string
  from: EmailMessageAddress[]
  to: EmailMessageAddress[]
  cc: EmailMessageAddress[]
  bcc: EmailMessageAddress[]
  replyTo: EmailMessageAddress[]
  metadata: EmailMessageMetadata | null
  attachments: MainAttachmentState[]
  showUnlocked: boolean
  parentHref?: string
  parentLabel?: string
}

export function EmailMessageView({
  model,
  embedded = false,
}: {
  model: EmailMessageViewModel
  embedded?: boolean
}) {
  const [showHtml, setShowHtml] = useState(false)
  const message = model.metadata
  const htmlBody = message?.htmlBody || model.bodyHtml
  const plainBody = message?.textBody || model.body
  const direction = message?.direction || (model.from.length ? 'inbound' : 'outbound')
  const displayedDate = formatDate(message?.sentAt || message?.receivedAt || 0, model.datetime)
  const source = direction === 'inbound' ? model.from : model.to

  return (
    <article
      {...(embedded ? {} : legacyHookAttrs('main.root'))}
      className={`md-email-message text-[#1f2d3d] ${embedded ? 'min-h-0 bg-white' : 'min-h-screen bg-[#eef1f4]'}`}
    >
      <div className={embedded
        ? 'min-h-0 bg-white'
        : 'mx-auto min-h-screen max-w-[1080px] bg-white shadow-[0_0_0_1px_rgba(50,65,80,0.08),0_12px_36px_rgba(31,45,61,0.08)]'}>
        <nav className={`${embedded ? 'hidden ' : ''}flex min-h-12 items-center justify-between gap-3 border-b border-[#dce3ea] bg-[#f7f9fb] px-5 py-2 sm:px-8`}>
          <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-[#58677a]">
            <span className={`h-2.5 w-2.5 rounded-full ${direction === 'inbound' ? 'bg-[#2f7d63]' : 'bg-[#386fa4]'}`} />
            {direction === 'inbound' ? 'Incoming email' : 'Outgoing email'}
          </div>
          {model.parentHref ? (
            <a className="rounded-md border border-[#cbd5df] bg-white px-3 py-1.5 text-[12px] font-medium text-[#31465c] hover:bg-[#edf2f6]" href={model.parentHref}>
              Back to {model.parentLabel || 'parent'}
            </a>
          ) : null}
        </nav>

        <header
          {...(embedded ? {} : legacyHookAttrs('main.header'))}
          className="!block border-b border-[#dde3ea] px-5 py-6 sm:px-8"
        >
          <h1
            {...(embedded ? {} : legacyHookAttrs('main.subjectText'))}
            className="break-words text-[24px] font-semibold leading-8 text-[#1d2939]"
          >
            {model.subject || '(No subject)'}
          </h1>
          <div className="mt-5 flex items-start gap-3">
            <div className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-[#3f6f87] text-[15px] font-semibold text-white">
              {initials(source, model.subject)}
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <AddressLine label="From" addresses={model.from} />
              <AddressLine label="To" addresses={model.to} />
              <AddressLine label="Cc" addresses={model.cc} />
              <AddressLine label="Bcc" addresses={model.bcc} />
              <AddressLine label="Reply-To" addresses={model.replyTo} />
            </div>
            {displayedDate ? <time className="hidden flex-none text-[12px] text-[#667085] sm:block">{displayedDate}</time> : null}
          </div>
          {displayedDate ? <time className="mt-3 block text-[12px] text-[#667085] sm:hidden">{displayedDate}</time> : null}
        </header>

        <section {...(embedded ? {} : legacyHookAttrs('main.body'))} className="!block">
          <div
            {...(embedded ? {} : legacyHookAttrs('main.iframe'))}
            className="md-email-message-body px-5 py-7 text-[14px] leading-6 text-[#263646] sm:px-8"
          >
            {!model.showUnlocked ? (
              <LockedScreen />
            ) : plainBody || htmlBody ? (
              <>
                {htmlBody ? (
                  <div className="mb-5 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#dce3ea] bg-[#f7f9fb] px-3 py-2">
                    <span className="text-[12px] text-[#5f6e7f]">
                      Plain text is shown by default. HTML may load remote images.
                    </span>
                    <button
                      type="button"
                      className="rounded-md border border-[#c8d3dd] bg-white px-3 py-1 text-[12px] font-medium text-[#30465d] hover:bg-[#edf2f6]"
                      onClick={() => setShowHtml(value => !value)}
                    >
                      {showHtml ? 'Show plain text' : 'Show HTML version'}
                    </button>
                  </div>
                ) : null}
                <ContentView
                  doc={showHtml && htmlBody
                    ? { kind: 'html', value: htmlBody }
                    : { kind: 'plain', value: plainBody || model.body }}
                />
              </>
            ) : (
              <EmptyContent label="This email has no message body." />
            )}
          </div>
          {!embedded ? <div {...legacyHookAttrs('main.editMount', 'hidden')} hidden aria-hidden="true" /> : null}
        </section>

        {model.showUnlocked ? <AttachmentList attachments={model.attachments} /> : null}
        {model.showUnlocked && message ? <TechnicalDetails message={message} /> : null}
      </div>
    </article>
  )
}

export function EmailMainMessageView() {
  const { state } = useSubsStore()
  const main = selectMainState(state)
  const access = selectAccessState(state)
  const parents = selectParentsMenu(state)
  const attachments = useMemo(() => selectMainAttachmentsState(state), [state])
  const metadata = main.email?.message ?? null
  const parent = parents.canSee ? parents.links[0] : undefined

  const model: EmailMessageViewModel = {
    subject: metadata?.subject || scalar(main.subject || main.title),
    body: scalar(main.body),
    bodyHtml: scalar(main.bodyHtml),
    datetime: scalar(main.datetime),
    from: metadata?.from.length ? metadata.from : tupleAddresses(main.email?.from),
    to: metadata?.to.length ? metadata.to : tupleAddresses(main.email?.to),
    cc: metadata?.cc ?? [],
    bcc: metadata?.bcc ?? [],
    replyTo: metadata?.replyTo ?? [],
    metadata,
    attachments,
    showUnlocked: access.showUnlocked,
    parentHref: parent ? `/${encodeURIComponent(parent.codeKey)}` : undefined,
    parentLabel: parent?.label,
  }

  return <EmailMessageView model={model} />
}
